import { Banner, Button, Checkbox, IconVideo, seismicTheme, Theme } from '@seismic/mantle';
import SeismicPlayer from '@seismic/universal-player';
import jwt_decode from 'jwt-decode';
import React, { ReactElement, useMemo, useRef, useState } from 'react';
import { Upload } from 'tus-js-client';
import { v4 as uuidv4 } from 'uuid';
import { UniversalVideoRecorder } from '../src';
import styles from './Demo.scss';

export default function Demo(): ReactElement {
  const [showModal, setShowModal] = useState(false);
  const [maxSeconds, setMaxSeconds] = useState(0);
  const [video, setVideo] = useState<Blob | null>(null);
  const [filename, setFilename] = useState<string | undefined>();
  const [content, setContent] = useState<string | null>(null);
  const filesRef = useRef<HTMLInputElement>(null);
  const [authToken, setAuthToken] = useState('');
  const [endpoint, setEndpoint] = useState<string>('');
  const [enableVimeoRecorder, setEnableVimeoRecorder] = useState(false);
  let endpoints = [{ url: '', name: '(None selected)', type: 'Local' }];

  const localEndpoints = [{ url: 'http://localhost:5000', name: 'Localhost', type: 'Local' }];
  const devEndpoints = [
    { url: 'https://uvrg-dev-az-westus-mongoose.seismic-dev.com', name: 'Azure Dev West US', type: 'Dev' },
    {
      url: 'https://uvrg-dev-az-eastasia-meerkat.seismic-dev.com',
      name: 'Azure Dev East Asia',
      type: 'Dev'
    },
    {
      url: 'https://uvrg-dev-gcp-asia-east2-chinchilla.seismic-dev.com',
      name: 'GCP Dev East Asia',
      type: 'Dev'
    },
    { url: 'https://uvrg-dev-gcp-us-west1-yak.seismic-dev.com', name: 'GCP Dev West US', type: 'Dev' },
    { url: 'https://uvrg-dev-ibm-us-south-raven.seismic-dev.com', name: 'IBM Dev US South', type: 'Dev' }
  ];

  const qaEndpoints = [
    { url: 'https://uvrg-qa-az-westus-wombat.seismic-dev.com', name: 'Azure QA West US', type: 'QA' },
    { url: 'https://uvrg-qa-az-eastasia-hyena.seismic-dev.com', name: 'Azure QA East Asia', type: 'QA' },
    { url: 'https://uvrg-qa-gcp-asia-east2-newt.seismic-dev.com', name: 'GCP QA East Asia', type: 'QA' },
    { url: 'https://uvrg-edge-qa-gcp-us-west1-goat.seismic-dev.com', name: 'GCP QA West US (Edge)', type: 'QA' },
    { url: 'https://uvrg-qa-ibm-us-south-wolf.seismic-dev.com', name: 'IBM QA US South', type: 'QA' }
  ];

  const uatEndpoints = [
    { url: 'https://uvrg-uat-az-westus-iguana.seismic-dev.com', name: 'Azure UAT West US', type: 'UAT' },
    { url: 'https://uvrg-uat-gcp-us-west1-leopard.seismic-dev.com', name: 'GCP UAT West US', type: 'UAT' }
  ];

  const prodEndpoints = [
    { url: 'https://uvrg-prod-az-westus-sloth.seismic.com', name: 'Azure West US', type: 'Prod' },
    { url: 'https://uvrg-prod-gcp-us-west1-kudu.seismic.com', name: 'GCP West US', type: 'Prod' },
    {
      url: 'https://uvrg-prod-az-australiasoutheast-tetra.seismic.com',
      name: 'Azure Australia Southeast',
      type: 'Prod'
    },
    { url: 'https://uvrg-prod-az-canadacentral-panda.seismic.com', name: 'Azure Canada Central', type: 'Prod' },
    { url: 'https://uvrg-prod-az-eastus-raptor.seismic.com', name: 'Azure East US', type: 'Prod' },
    { url: 'https://uvrg-prod-az-westeurope-llama.seismic.com', name: 'Azure West Europe', type: 'Prod' },
    { url: 'https://uvrg-prod-ibm-us-east-otter.seismic.com', name: 'IBM East US', type: 'Prod' }
  ];

  if (window.location.hostname === 'localhost') {
    endpoints = endpoints.concat(localEndpoints);
  } else if (window.location.hostname.endsWith('seismic-dev.com')) {
    endpoints = endpoints.concat(devEndpoints);
    endpoints = endpoints.concat(qaEndpoints);
    endpoints = endpoints.concat(uatEndpoints);
  } else if (window.location.hostname.endsWith('seismic.com')) {
    endpoints = endpoints.concat(prodEndpoints);
  } else {
    throw new Error(
      'Invalid host - Due to CORS configuration, you must deploy this demo application to a localhost, seismic-dev.com, or seismic.com domain'
    );
  }

  const onJwtClick = () => {
    window.open('https://cdn-prod.seismic.com/static/public/getatoken/index.html', '_blank');
  };

  const onMaxSecondsChanged = (event) => {
    if (event.target.value === '') {
      setMaxSeconds(0);
      return;
    }

    const value = parseInt(event.target.value);
    setMaxSeconds(value);
  };

  function openUvr(): void {
    setShowModal(true);
  }

  const fakeManifest = useMemo((): any => {
    return {
      ContentManifestId: uuidv4(),
      AllowDownload: true,
      AllowPrint: true,
      Content: {
        TeamsiteId: '',
        Id: uuidv4(),
        VersionId: uuidv4(),
        Repository: 'library',
        Type: 'file'
      },
      CoverImageUrl: '',
      Manifests: [
        {
          FormatType: 'Video',
          NativeFormat: 'webm',
          Url: video ? `${URL.createObjectURL(video)}#t=0.001` : '',
          DownloadUrl: video ? URL.createObjectURL(video) : '',
          Mode: 'read',
          Title: 'Mp4',
          ViewerDetail: {},
          Permissions: {
            AllowDownload: false,
            AllowPrint: false
          },
          Error: null,
          ErrorReason: 'None',
          DowngradeReason: null,
          IsCacheable: true
        }
      ],
      Modes: ['read'],
      LogUrl: ''
    };
  }, [video]);

  const onClose = (video: Blob | null, filename?: string): void => {
    setShowModal(false);
    if (video) {
      setVideo(video);
    }

    setFilename(filename);
  };

  const handleComplete = (videoId?: string) => {
    console.log('UVR complete', videoId);
    setShowModal(false);
  };
  const handleInitialize = () => console.log('UVR initialize');
  const handleRecordingCancel = () => console.log('UVR recording cancel');
  const handleRecordingStart = () => console.log('UVR recording start');
  const handleRecordingStop = () => console.log('UVR recording stop');

  const onProcessingComplete = (content: string | null | Error) => {
    if (content) {
      if (content instanceof Error) {
        console.error(content);
        setContent('An error occurred during processing!');
      } else {
        setContent(content);
      }
    }
  };

  const onTokenChange = (e) => {
    const token = e.target.value;
    try {
      // Just try to decode the token so that we prevent bad tokens from being used
      jwt_decode(token);
      setAuthToken(token);
    } catch (err) {
      setAuthToken('');
      console.error(err);
    }
  };

  const handleFileClickTus = async () => {
    if (!filesRef || !filesRef.current || !filesRef.current.files || !filesRef.current.files[0]) {
      return;
    }

    const upload = new Upload(filesRef.current.files[0], {
      headers: {
        Authorization: `Bearer ${authToken}`
      },
      chunkSize: 15000000, // Seems like our nginx proxy max size is currently set to 20MB (This can be overridden, but I'm not doing that right now)
      endpoint: `${endpoint}/api/v1/upload`,
      retryDelays: [0, 3000, 5000, 10000],
      // metadata: {
      //   filename: state.fileName ?? 'default',
      //   filetype: video.type
      // },
      onBeforeRequest: function (req) {
        var xhr = req.getUnderlyingObject();
        xhr.withCredentials = true;
      },
      onError: function (error) {
        console.error('Failed to upload video to server with error {Error}', error);
      },
      onProgress: function (bytesUploaded, bytesTotal) {
        const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0);
        console.log(
          'BytesUploaded {BytesUploaded}, BytesTotal {BytesTotal}, Percentage {Percentage}',
          bytesUploaded,
          bytesTotal,
          percentage + '%'
        );
      },
      onSuccess: async function () {
        console.log('Upload complete');
      }
    });

    upload.start();
  };

  const onEnableVimeoRecorder = (event) => {
    setEnableVimeoRecorder(event.target.checked);
  };

  return (
    <Theme theme={seismicTheme({ useGlobal: true })}>
      <div className={styles.container}>
        <div className={styles.controlsContainer}>
          {(!authToken || !endpoint) && (
            <Banner
              variant="info"
              message="You must specify an auth token and endpoint in order to upload to Seismic"
            />
          )}
          <div>
            Select Endpoint:&nbsp;
            <select onChange={(e) => setEndpoint(e.target.value)}>
              {endpoints.map((e, index) => {
                return (
                  <option value={e.url} key={index}>
                    {e.name}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <textarea
              style={{ width: '100%', height: '100px' }}
              placeholder="Paste JWT token here"
              onChange={onTokenChange}
            />
            <button style={{ width: '15%' }} onClick={onJwtClick}>
              Get JWT token
            </button>
          </div>
          <div>
            <div>Customize max recording length: </div>
            <span>Seconds: </span>
            <input
              style={{ width: '100%' }}
              type="number"
              placeholder="Add length in seconds..."
              onChange={onMaxSecondsChanged}
            />
          </div>

          <div>
            Test Manual File Upload:
            <input type="file" name="file" id="file" ref={filesRef} />
            <button onClick={handleFileClickTus} name="submit">
              Upload File
            </button>
          </div>
          <div>
            Enable Vimeo Recorder <Checkbox checked={enableVimeoRecorder} onChange={onEnableVimeoRecorder} />
          </div>
        </div>
        <div className={styles.previewContainer}>
          <div>
            <Button
              label="Open UVR"
              data-testid="openUvrButton"
              startAdornment={<IconVideo size={24} />}
              onClick={openUvr}
            />
          </div>
          {content && <div>{JSON.stringify(content)}</div>}
          <div>Filename: {filename}</div>
          {video && (
            <div className={styles.preview}>
              <SeismicPlayer config={fakeManifest} verifySignature={false} />
            </div>
          )}
          <UniversalVideoRecorder
            open={showModal}
            enableVimeoRecorder={enableVimeoRecorder}
            maxRecordTimeSeconds={maxSeconds}
            onClose={onClose}
            onComplete={handleComplete}
            onInitialize={handleInitialize}
            onRecordingCancel={handleRecordingCancel}
            onRecordingStart={handleRecordingStart}
            onRecordingStop={handleRecordingStop}
            onProcessingComplete={onProcessingComplete}
            gatewayUrl={endpoint}
            getAuthToken={() => authToken}
          />
        </div>
      </div>
    </Theme>
  );
}
